import { useEffect, useState } from 'react';
import type { TagWithCount } from '@mlc/shared';
import { deleteTag, listTags, renameTag } from './api';
import { Banner, Button, Loading, TAG_TONE_CLASSES, TextInput } from './ui';
import { TagRules } from './TagRules';

/**
 * Gestion des étiquettes manuelles (US-21) : renommer, supprimer. La création
 * n'est pas ici — une étiquette se crée en la posant sur une recette (écran
 * d'édition), jamais dans un écran d'administration, sans quoi on se retrouve
 * avec une liste d'étiquettes que rien ne porte.
 *
 * Les étiquettes **calculées** sont listées à côté, en lecture seule : elles
 * existent, l'utilisateur les voit dans la barre de filtres, et ne pas les
 * mentionner ici laisserait croire qu'il peut les supprimer ailleurs.
 */
export function TagManager(): React.JSX.Element {
  const [tags, setTags] = useState<TagWithCount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    listTags(controller.signal)
      .then(setTags)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
      });
    return () => { controller.abort(); };
  }, []);

  function commitRename(id: string, name: string): void {
    const cleaned = name.trim();
    if (cleaned === '') { setEditing(null); return; }
    setBusy(true);
    setError(null);
    renameTag(id, cleaned)
      .then((updated) => {
        setTags((current) => current?.map((tag) => (tag.id === id ? { ...tag, name: updated.name } : tag)) ?? null);
        setEditing(null);
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  function remove(tag: TagWithCount): void {
    setBusy(true);
    setError(null);
    deleteTag(tag.id)
      .then(() => { setTags((current) => current?.filter((other) => other.id !== tag.id) ?? null); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  if (tags === null) {
    return <Loading label="les étiquettes" lines={2} />;
  }

  const manual = tags.filter((tag) => tag.kind === 'manual');

  return (
    <div className="flex flex-col gap-4">
      {error !== null && <Banner tone="danger">{error}</Banner>}

      {manual.length === 0 ? (
        <p className="text-sm text-foreground-secondary">
          Aucune étiquette pour l’instant. Elles se créent en les posant sur une recette, depuis l’écran de
          modification.
        </p>
      ) : (
        <ul data-testid="tag-manager" className="flex flex-col gap-2">
          {manual.map((tag) => (
            <li key={tag.id} className="flex flex-wrap items-center gap-2 border-t border-border pt-2 first:border-t-0 first:pt-0">
              {editing?.id === tag.id ? (
                <>
                  <label htmlFor={`tag-name-${tag.id}`} className="sr-only">
                    Nouveau nom de l’étiquette {tag.name}
                  </label>
                  <TextInput
                    id={`tag-name-${tag.id}`}
                    value={editing.name}
                    autoFocus
                    disabled={busy}
                    onChange={(e) => { setEditing({ id: tag.id, name: e.target.value }); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(tag.id, editing.name); }}
                    className="w-48"
                  />
                  <Button type="button" variant="secondary" size="compact" disabled={busy} onClick={() => { commitRename(tag.id, editing.name); }}>
                    Renommer
                  </Button>
                  <Button type="button" variant="ghost" size="compact" onClick={() => { setEditing(null); }}>
                    Annuler
                  </Button>
                </>
              ) : (
                <>
                  <span className={`rounded-full px-3 py-1 ${TAG_TONE_CLASSES.manual}`}>{tag.name}</span>
                  <span className="text-sm text-foreground-muted">
                    <span className="text-data">{tag.recipe_count}</span>
                    {tag.recipe_count > 1 ? ' recettes' : ' recette'}
                  </span>
                  <span className="flex-1" />
                  <Button type="button" variant="ghost" size="compact" onClick={() => { setEditing({ id: tag.id, name: tag.name }); }}>
                    Renommer
                  </Button>
                  {/* Seule suppression du produit, et elle ne porte que sur un
                      rangement : « on archive, on ne supprime jamais » vise les
                      recettes (§3 CLAUDE.md). Le libellé le dit, pour que le
                      geste ne se confonde pas avec la suppression d'une
                      recette — qui, elle, n'existe pas. */}
                  {/* `secondary` et non `ghost` (US-57) : une action
                      destructive et irréversible ne se présente pas comme un
                      lien souligné. Et elle demande confirmation — supprimer
                      une étiquette emporte le rangement de **tous** les
                      membres, ce que rien ne disait. */}
                  {aSupprimer === tag.id ? (
                    <>
                      <span className="text-sm text-foreground-secondary">
                        Retirer « {tag.name} » de {tag.recipe_count > 1 ? 'toutes les recettes' : 'la recette'} qui la porte, pour tout le monde ?
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="compact"
                        disabled={busy}
                        onClick={() => { setASupprimer(null); remove(tag); }}
                      >
                        Confirmer
                      </Button>
                      <Button type="button" variant="ghost" size="compact" onClick={() => { setASupprimer(null); }}>
                        Annuler
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="compact"
                      disabled={busy}
                      onClick={() => { setASupprimer(tag.id); }}
                    >
                      Supprimer l’étiquette
                    </Button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Les étiquettes calculées étaient listées ici en lecture seule, sous
          la phrase « elles ne peuvent pas être modifiées ». Elle est devenue
          fausse en US-61 : leurs règles sont réglables, et c'est `TagRules` qui
          les porte. Les nommer sans les régler n'apprenait rien. */}
      <TagRules />
    </div>
  );
}
